import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, Card } from "@/components/page-shell";
import { InlineLinks } from "@/components/checklist";
import type { Article } from "@/lib/articles";
import { GUIDES } from "@/lib/guides";
import { absUrl } from "@/lib/site";
import { pageTitle } from "@/lib/seo";

/** What the index shows of each article: no bodies, so the list stays small. */
type ArticleSummary = Pick<
  Article,
  "slug" | "title" | "description" | "category" | "readMinutes" | "updated"
>;

export const Route = createFileRoute("/resources/")({
  // The articles module (25KB of prose) is imported here rather than at the top: this
  // route's head() is part of the entry chunk every page downloads, and it only needs
  // the titles.
  loader: async (): Promise<{
    articles: ArticleSummary[];
    categories: readonly Article["category"][];
  }> => {
    const { ARTICLES, ARTICLE_CATEGORIES } = await import("@/lib/articles");
    return {
      articles: ARTICLES.map(({ slug, title, description, category, readMinutes, updated }) => ({
        slug,
        title,
        description,
        category,
        readMinutes,
        updated,
      })),
      categories: ARTICLE_CATEGORIES,
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: pageTitle("Tick & Alpha-Gal Articles and Guides") },
      {
        name: "description",
        content:
          "Every guide on LoneStarMap, by topic: what to do after a tick bite, how to tell ticks apart, alpha-gal syndrome, prevention, pets, yards and the outdoors.",
      },
      { property: "og:title", content: "Tick & alpha-gal guides, by topic" },
      {
        property: "og:description",
        content:
          "Free guides on Lone Star ticks, alpha-gal syndrome, prevention, and outdoor safety.",
      },
      { property: "og:url", content: absUrl("/resources") },
    ],
    links: [{ rel: "canonical", href: absUrl("/resources") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Tick & alpha-gal guides, by topic",
          url: absUrl("/resources"),
          mainEntity: {
            "@type": "ItemList",
            itemListElement: [
              ...GUIDES.map((g) => ({ name: g.h1, path: `/${g.slug}` })),
              ...(loaderData?.articles ?? []).map((a) => ({
                name: a.title,
                path: `/resources/${a.slug}`,
              })),
            ].map((item, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: item.name,
              url: absUrl(item.path),
            })),
          },
        }),
      },
    ],
  }),
  component: ResourcesIndex,
});

/** The site's tools, filed under the topic each one answers. */
const TOOLS = [
  {
    topic: "After a Bite",
    to: "/tick-bite-tool",
    title: "Tick bite tool",
    description:
      "Remove the tick step by step, log the bite on your device, and get a symptom timeline counted from that day.",
  },
  {
    topic: "After a Bite",
    to: "/tick-bite-guide",
    title: "Printable tick bite plan",
    description: "One page: how to remove a tick, what not to do, and when to call a doctor.",
  },
  {
    topic: "Identification",
    to: "/tick-identification",
    title: "Identify a tick",
    description: "Narrow it down by size, markings, leg colour and where you found it.",
  },
  {
    topic: "Identification",
    to: "/ticks",
    title: "Tick species field guide",
    description:
      "Photos, range and diseases for 8 common ticks that bite people and pets in the US.",
  },
  {
    topic: "Alpha-Gal",
    to: "/alpha-gal",
    title: "Alpha-gal syndrome",
    description:
      "The red meat allergy linked to Lone Star tick bites: symptoms, triggers and tests.",
  },
  {
    topic: "Diseases",
    to: "/diseases",
    title: "Tick-borne diseases",
    description: "Symptoms, how soon they start, and which tick carries each illness.",
  },
  {
    topic: "Prevention",
    to: "/prevention",
    title: "Prevention guide",
    description: "Repellents, treated clothing, and the tick check that catches nymphs.",
  },
  {
    topic: "Maps & Seasons",
    to: "/map",
    title: "Lone Star tick map",
    description: "Where the tick is found, state by state and county by county.",
  },
  {
    topic: "Maps & Seasons",
    to: "/states",
    title: "Every state",
    description: "Each state's CDC county count, peak season and county pages.",
  },
] as const satisfies ReadonlyArray<{
  topic: Article["category"];
  to: string;
  title: string;
  description: string;
}>;

type Entry =
  | {
      kind: "Tool";
      key: string;
      title: string;
      description: string;
      to: (typeof TOOLS)[number]["to"];
    }
  | { kind: "Guide"; key: string; title: string; description: string; slug: string }
  | {
      kind: "Article";
      key: string;
      title: string;
      description: string;
      slug: string;
      minutes: number;
    };

function ResourcesIndex() {
  const { articles, categories } = Route.useLoaderData();
  const topics = categories
    .map((cat) => {
      const entries: Entry[] = [
        ...TOOLS.filter((t) => t.topic === cat).map(
          (t): Entry => ({
            kind: "Tool",
            key: t.to,
            title: t.title,
            description: t.description,
            to: t.to,
          }),
        ),
        ...GUIDES.filter((g) => g.topic === cat).map(
          (g): Entry => ({
            kind: "Guide",
            key: g.slug,
            title: g.h1,
            description: g.metaDescription,
            slug: g.slug,
          }),
        ),
        ...articles
          .filter((a) => a.category === cat)
          .map(
            (a): Entry => ({
              kind: "Article",
              key: a.slug,
              title: a.title,
              description: a.description,
              slug: a.slug,
              minutes: a.readMinutes,
            }),
          ),
      ];
      return { cat, id: `topic-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`, entries };
    })
    .filter((t) => t.entries.length > 0);

  return (
    <PageShell
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Resources", path: "/resources" },
      ]}
      title="Tick & alpha-gal guides, by topic"
      intro="Tools, short answers and longer reads, filed by what you need: what to do after a bite, how to tell the ticks apart, alpha-gal, prevention, pets and yards, and the outdoors."
    >
      <nav aria-label="Topics" className="no-print">
        <InlineLinks className="text-sm">
          {topics.map((t) => (
            <li key={t.id}>
              <a href={`#${t.id}`} className="link">
                {t.cat}
              </a>
            </li>
          ))}
        </InlineLinks>
      </nav>
      <Card>
        <div className="space-y-10">
          {topics.map(({ cat, id, entries }) => (
            <section
              key={cat}
              aria-labelledby={`${id}-h`}
              className="grid scroll-mt-24 gap-x-10 gap-y-2 lg:grid-cols-[11rem_1fr]"
              id={id}
            >
              <h2 id={`${id}-h`} className="label-caps pt-1 text-rust-ink lg:pt-5">
                {cat}
              </h2>
              <ul className="divide-y divide-border border-t border-border">
                {entries.map((e) => (
                  <EntryRow key={`${e.kind}-${e.key}`} entry={e} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      </Card>
    </PageShell>
  );
}

/** One entry: what it is, its title, a line on what it covers. */
function EntryRow({ entry: e }: { entry: Entry }) {
  const cls = "after:absolute after:inset-0 after:content-['']";
  return (
    <li className="group relative py-4">
      <p className="label-caps text-muted-foreground">
        {e.kind}
        {e.kind === "Article" && (
          <span className="tabular normal-case"> · {e.minutes} min read</span>
        )}
      </p>
      <h3 className="mt-1 font-display text-xl font-medium leading-snug text-foreground transition-colors group-hover:text-primary">
        {e.kind === "Tool" ? (
          <Link to={e.to} className={cls}>
            {e.title}
          </Link>
        ) : e.kind === "Guide" ? (
          <Link to="/$guide" params={{ guide: e.slug }} className={cls}>
            {e.title}
          </Link>
        ) : (
          <Link to="/resources/$slug" params={{ slug: e.slug }} className={cls}>
            {e.title}
          </Link>
        )}
      </h3>
      <p className="measure mt-1 text-[0.9375rem] leading-relaxed text-muted-foreground">
        {e.description}
      </p>
    </li>
  );
}
